import type { NexoyaPortfolioContentLabelFilterInput } from '../types';
import { CONTENT_LABEL_NEEDS_DECISION_ID, CONTENT_LABEL_NULL_BUCKET_ID } from '../utils/contentLabelBucket';

// Routes-free helpers over a Content Labels v2 selection, shared by the Performance filters (through
// performanceFilterSelection) and the Content tab's label filter store.

/**
 * `Map<contentLabelCategoryId, Set<valueId | sentinel>>`, in click order. The sentinels are
 * `CONTENT_LABEL_NULL_BUCKET_ID` (Unlabeled) and, on the Content tab only, `CONTENT_LABEL_NEEDS_DECISION_ID`.
 */
export type ContentLabelSelection = Map<string, Set<string>>;

const SENTINEL_IDS: ReadonlySet<string> = new Set([CONTENT_LABEL_NULL_BUCKET_ID, CONTENT_LABEL_NEEDS_DECISION_ID]);

/** The categories a selection is checked against; `LabelCategory[]` fits it as is. */
export type ContentLabelCatalog = ReadonlyArray<{
  labelCategoryId: string;
  labels: ReadonlyArray<{ labelId: string }>;
}>;

/**
 * Toggles one value, or a sentinel (Unlabeled, Need decision), in one category. An emptied category is DELETED,
 * so `contentLabels.size` stays the active-category count and an emptied category never reaches the wire.
 */
export function toggleContentLabelValueIn(
  state: { contentLabels: ContentLabelSelection },
  contentLabelCategoryId: string,
  valueId: string,
): { contentLabels: ContentLabelSelection } {
  const next = new Map(state.contentLabels);
  const values = new Set(next.get(contentLabelCategoryId));
  if (values.has(valueId)) values.delete(valueId);
  else values.add(valueId);

  if (values.size === 0) next.delete(contentLabelCategoryId);
  else next.set(contentLabelCategoryId, values);

  return { contentLabels: next };
}

/** Drops one whole content-label category (a section's "Clear all", or its chip ×). */
export function clearContentLabelCategoryIn(
  state: { contentLabels: ContentLabelSelection },
  contentLabelCategoryId: string,
): { contentLabels: ContentLabelSelection } {
  const next = new Map(state.contentLabels);
  next.delete(contentLabelCategoryId);
  return { contentLabels: next };
}

/**
 * Removes what the catalog no longer has: unknown categories (their Unlabeled and Need decision ticks too),
 * unknown values, and categories left empty. A known category keeps its sentinels. Returns the SAME Map
 * when nothing is stale, so a store can skip notifying.
 */
export function pruneContentLabelSelection(
  selection: ContentLabelSelection,
  catalog: ContentLabelCatalog,
): ContentLabelSelection {
  const knownValueIds = new Map(
    catalog.map((category) => [category.labelCategoryId, new Set(category.labels.map(({ labelId }) => labelId))]),
  );
  const isKnown = (contentLabelCategoryId: string, valueId: string): boolean => {
    const valueIds = knownValueIds.get(contentLabelCategoryId);
    return valueIds !== undefined && (SENTINEL_IDS.has(valueId) || valueIds.has(valueId));
  };
  const isStale = [...selection].some(
    ([contentLabelCategoryId, valueIds]) =>
      valueIds.size === 0 || [...valueIds].some((valueId) => !isKnown(contentLabelCategoryId, valueId)),
  );
  if (!isStale) return selection;

  return new Map(
    [...selection]
      .map(([contentLabelCategoryId, valueIds]): [string, Set<string>] => [
        contentLabelCategoryId,
        new Set([...valueIds].filter((valueId) => isKnown(contentLabelCategoryId, valueId))),
      ])
      .filter(([, valueIds]) => valueIds.size > 0),
  );
}

/**
 * The wire form of `PortfolioContentFilterInput.contentLabelFilters`. Values and entries are SORTED because
 * the Map keeps click order and Apollo keys its cache on array order. The sentinels become `includeUnlabeled`
 * and `includeNeedsDecision`, never value ids, and an entry that would filter nothing is dropped.
 */
export function toPortfolioContentLabelFilters(
  selection: ContentLabelSelection,
): NexoyaPortfolioContentLabelFilterInput[] {
  return [...selection]
    .map(([contentLabelCategoryId, valueIds]) => ({
      contentLabelCategoryId,
      contentLabelCategoryValueIds: [...valueIds].filter((valueId) => !SENTINEL_IDS.has(valueId)).sort(),
      includeUnlabeled: valueIds.has(CONTENT_LABEL_NULL_BUCKET_ID),
      includeNeedsDecision: valueIds.has(CONTENT_LABEL_NEEDS_DECISION_ID),
    }))
    .filter(
      (entry) => entry.contentLabelCategoryValueIds.length > 0 || entry.includeUnlabeled || entry.includeNeedsDecision,
    )
    .sort((a, b) => a.contentLabelCategoryId.localeCompare(b.contentLabelCategoryId));
}
